import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { getDoctorDetails, getAvailableAppointmentSlots } from '../services/openapis';
import BookMyOpAPI from '../services/BookMyOpapi';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import BookmyopLayout from './BookmyopLayout';
import { config } from '../config';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Avatar,
    Divider,
    Box,
    Chip,
    Button,
    CircularProgress,
    Alert,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Snackbar,
    Tabs,
    Tab,
    Stack,
    useTheme,
    useMediaQuery,
    TextField,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import {
    LocalHospital,
    AccessTime,
    LocationOn,
    Money,
    Discount,
    ArrowBack,
    Person,
    Schedule,
    Star,
    Verified,
    Phone,
    Email,
} from '@mui/icons-material';
import DialogContentText from '@mui/material/DialogContentText';

// Helper function to get the next occurrence of a day (0 = Monday, 6 = Sunday)
const getNextDayOccurrence = (dayOfWeek) => {
    const today = new Date();
    const currentDay = today.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    
    // Convert API day_of_week (0=Monday, 6=Sunday) to JavaScript day (0=Sunday, 6=Saturday)
    const apiDayToJsDay = (apiDay) => {
        // API: 0=Monday, 1=Tuesday, 2=Wednesday, 3=Thursday, 4=Friday, 5=Saturday, 6=Sunday
        // JS:  0=Sunday, 1=Monday, 2=Tuesday, 3=Wednesday, 4=Thursday, 5=Friday, 6=Saturday
        return apiDay === 6 ? 0 : apiDay + 1;
    };
    
    const targetJsDay = apiDayToJsDay(dayOfWeek);
    
    let daysToAdd = targetJsDay - currentDay;
    if (daysToAdd <= 0) {
        daysToAdd += 7;
    }

    const nextOccurrence = new Date(today);
    nextOccurrence.setDate(today.getDate() + daysToAdd);
    return nextOccurrence;
};

const formatDate = (date) => {
    const options = {
        weekday: 'short',
        month: 'short',
        day: 'numeric'
    };
    return date.toLocaleDateString('en-US', options);
};

// Helper function to format date as YYYY-MM-DD in local timezone (not UTC)
const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

// Custom TabPanel component
function TabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`doctor-tabpanel-${index}`}
            aria-labelledby={`doctor-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 0 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}


const DoctorDetail = () => {
    const { doctorId } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { user, isAuthenticated } = useBookMyOpAuth();
    const [doctor, setDoctor] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedDay, setSelectedDay] = useState(null);
    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedSlotForBooking, setSelectedSlotForBooking] = useState(null);
    const [availableSlots, setAvailableSlots] = useState([]);
    const [loadingAllSlots, setLoadingAllSlots] = useState(false);
    const [slotsError, setSlotsError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const [creatingAppointment, setCreatingAppointment] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [confirmationDialog, setConfirmationDialog] = useState(false);

    console.log('Route debugging:', {
        doctorId,
        pathname: location.pathname,
        params: useParams(),
        fullLocation: location
    });

    // Extract doctor ID from URL if not in params
    const getDoctorId = () => {
        if (doctorId) return doctorId;
        const pathParts = location.pathname.split('/');
        const id = pathParts[pathParts.indexOf('doctors') + 1];
        return id || null;
    };

    useEffect(() => {
        const fetchDoctorDetails = async () => {
            const id = getDoctorId();
            console.log('Attempting to fetch with ID:', id);

            if (!id) {
                console.error('No doctor ID available');
                setError('Doctor ID is missing');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                setError(null);
                const response = await getDoctorDetails(id);
                console.log('API call successful, response:', response);

                if (response && response.data) {
                    setDoctor(response.data);
                    // Only set initial date if no date filter was passed in navigation state
                    const hasDateFilter = location.state?.dateFilters &&
                        (location.state.dateFilters.availableDate ||
                         location.state.dateFilters.availableDateStart ||
                         location.state.dateFilters.availableDateEnd);

                    if (!hasDateFilter && response.data.time_slots && response.data.time_slots.length > 0) {
                        const firstDayOfWeek = response.data.time_slots[0].day_of_week;
                        setSelectedDay(firstDayOfWeek);
                        // Also set the initial date
                        const initialDate = getNextDayOccurrence(firstDayOfWeek);
                        setSelectedDate(initialDate);
                    }
                } else {
                    throw new Error('Invalid response format from server');
                }
            } catch (err) {
                console.error('Error in fetchDoctorDetails:', err);
                setError(err.message || 'Failed to fetch doctor details');
                setDoctor(null);
            } finally {
                setLoading(false);
            }
        };

        fetchDoctorDetails();
    }, [doctorId, location]);

    const handleBack = () => {
        navigate('/find-doctors');
    };

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Helper function to check if a date is in upcoming breaks
    const isDateInBreaks = (date) => {
        if (!doctor?.upcoming_breaks) return false;

        // Use local timezone formatting to match API date format
        const dateString = formatDateForAPI(date);
        return doctor.upcoming_breaks.some(breakItem =>
            breakItem.break_date === dateString && breakItem.is_active
        );
    };

    // Helper function to check if a date is today or in the future
    const isDateNotPast = (date) => {
        if (!date) return false;
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const checkDate = new Date(date);
        checkDate.setHours(0, 0, 0, 0);
        return checkDate >= today;
    };

    // Helper function to check if a date is a valid day for this doctor
    const isDateValidForDoctor = (date) => {
        if (!doctor?.time_slots) return false;
        
        const jsDay = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        // Convert JS day to API day format (0 = Monday, 6 = Sunday)
        const apiDay = jsDay === 0 ? 6 : jsDay - 1;
        
        return doctor.time_slots.some(slot => slot.day_of_week === apiDay);
    };

    // Set initial date from navigation state if date filter was passed
    useEffect(() => {
        const dateFilters = location.state?.dateFilters;
        if (dateFilters && doctor) {
            let dateToSet = null;
            
            // Priority: availableDate > availableDateStart > availableDateEnd
            if (dateFilters.availableDate) {
                dateToSet = new Date(dateFilters.availableDate);
            } else if (dateFilters.availableDateStart) {
                dateToSet = new Date(dateFilters.availableDateStart);
            } else if (dateFilters.availableDateEnd) {
                dateToSet = new Date(dateFilters.availableDateEnd);
            }
            
            // Only set the date if it's valid for this doctor and not in breaks
            if (dateToSet && isDateValidForDoctor(dateToSet) && !isDateInBreaks(dateToSet) && isDateNotPast(dateToSet)) {
                setSelectedDate(dateToSet);
                setSelectedDay(null); // Clear selected day since we're using custom date
            }
        }
    }, [doctor, location.state]);

    // Get time slots for a specific date
    const getTimeSlotsForDate = (date) => {
        if (!doctor?.time_slots || !date) return [];

        const jsDay = date.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
        // Convert JS day to API day format (0 = Monday, 6 = Sunday)
        const apiDay = jsDay === 0 ? 6 : jsDay - 1;
        
        const slotsForDay = doctor.time_slots.filter(slot => slot.day_of_week === apiDay);
        
        // Sort slots by start time
        return slotsForDay.sort((a, b) => a.start_time.localeCompare(b.start_time));
    };

    const handleDateChange = (date) => {
        if (date && isDateValidForDoctor(date) && !isDateInBreaks(date)) {
            setSelectedDate(date);
            setSelectedDay(null); // Clear selected day since we're using custom date
            setAvailableSlots([]); // Clear previous slots
            setSlotsError(null); // Clear previous errors
        }
    };

    // Auto-fetch all appointment slots when date is selected
    useEffect(() => {
        const fetchAllAppointmentSlots = async () => {
            if (!selectedDate || !doctor?.time_slots) {
                setAvailableSlots([]);
                return;
            }

            // Check if date is valid and not in breaks
            if (!isDateValidForDoctor(selectedDate) || isDateInBreaks(selectedDate)) {
                setAvailableSlots([]);
                return;
            }

            const id = getDoctorId();
            if (!id) {
                return;
            }

            try {
                setLoadingAllSlots(true);
            setSlotsError(null);

                // Get all time slots for the selected date
                const timeSlotsForDate = getTimeSlotsForDate(selectedDate);
                
                if (timeSlotsForDate.length === 0) {
                    setAvailableSlots([]);
                    setLoadingAllSlots(false);
                    return;
                }

                // Fetch appointment slots for all time slots in parallel
                const slotPromises = timeSlotsForDate.map(slot => 
                    getAvailableAppointmentSlots(id, slot.id, selectedDate)
                        .catch(err => {
                            console.error(`Error fetching slots for time slot ${slot.id}:`, err);
                            return { available_slots: [], booked_slots: [] };
                        })
                );

                const responses = await Promise.all(slotPromises);
                
                // Combine all available and booked slots
                const allSlots = [];
                responses.forEach(response => {
                    if (response && response.available_slots) {
                        allSlots.push(...response.available_slots.map(slot => ({ ...slot, status: 'available' })));
                    }
                    if (response && response.booked_slots) {
                        allSlots.push(...response.booked_slots.map(slot => ({ ...slot, status: 'booked' })));
                    }
                });

                // Sort by start time
                allSlots.sort((a, b) => a.start.localeCompare(b.start));

                setAvailableSlots(allSlots);
            } catch (err) {
                console.error('Error fetching all appointment slots:', err);
                setSlotsError(err.message || 'Failed to fetch available slots');
                setAvailableSlots([]);
            } finally {
                setLoadingAllSlots(false);
            }
        };

        fetchAllAppointmentSlots();
    }, [selectedDate, doctor]);

    const handleChipClick = (slot) => {
        if (slot.status === 'booked') {
            return; // Don't allow clicking booked slots
        }
        
        if (!isAuthenticated) {
            setSnackbar({
                open: true,
                message: 'Please login to book an appointment',
                severity: 'warning'
            });
            navigate('/login', { state: { from: location } });
            return;
        }

        setSelectedSlotForBooking(slot);
        setConfirmationDialog(true);
    };

    const handleSelectAppointment = async (slot) => {
        if (!slot) return;

        try {
            setCreatingAppointment(true);

            // Use the stored selected date (same date shown to user in UI)
            if (!selectedDate) {
                throw new Error('Please select a date first');
            }

            // Format date in local timezone (not UTC) to match what user sees
            const appointmentDateString = formatDateForAPI(selectedDate);

            // Create FormData object
            const formData = new FormData();

            // Add basic appointment details
            formData.append('service_provider', Number(doctor.service_provider.id));
            formData.append('specialty', Number(doctor.specialty.id));
            formData.append('doctor', Number(doctor.id));
            formData.append('appointment_date', appointmentDateString);
            // Available slots have 'start' property
            formData.append('appointment_time', slot.start.substring(0, 5));
            
            console.log('Sending appointment data:', {
                selectedDateObject: selectedDate,
                appointmentDateString: appointmentDateString,
                displayedDate: formatDate(selectedDate),
                service_provider: Number(doctor.service_provider.id),
                specialty: Number(doctor.specialty.id),
                doctor: Number(doctor.id),
                appointment_date: appointmentDateString,
                appointment_time: slot.start.substring(0, 5)
            });

            const response = await BookMyOpAPI.createAppointment(formData);

            setSnackbar({
                open: true,
                message: 'Appointment requested. You will get an update soon.',
                severity: 'success'
            });

            setConfirmationDialog(false);
            setSelectedSlotForBooking(null);
            
            // Redirect to appointments page after a brief delay to show success message
            setTimeout(() => {
                navigate('/appointments');
            }, 1500);

        } catch (err) {
            console.error('Error creating appointment:', err);
            let errorMessage = 'Failed to book appointment';

            // Handle structured error response
            if (err.errors) {
                const errorMessages = [];
                Object.entries(err.errors).forEach(([field, messages]) => {
                    errorMessages.push(`${field}: ${messages.join(', ')}`);
                });
                errorMessage = errorMessages.join('\n');
            } else if (err.message) {
                errorMessage = err.message;
            }

            setSnackbar({
                open: true,
                message: errorMessage,
                severity: 'error'
            });
        } finally {
            setCreatingAppointment(false);
        }
    };

    // Confirm booking (call API)
    const handleConfirmBooking = async () => {
        setConfirmationDialog(false);
        await handleSelectAppointment(selectedSlotForBooking);
    };

    // Cancel booking (just close modal)
    const handleCancelBooking = () => {
        setConfirmationDialog(false);
        setSelectedSlotForBooking(null);
    };

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    // Helper function to get the next 3 months date range
    const getDateRange = () => {
        const today = new Date();
        const threeMonthsFromNow = new Date();
        threeMonthsFromNow.setMonth(today.getMonth() + 3);
        return { minDate: today, maxDate: threeMonthsFromNow };
    };

    // Group time slots by day and add dates
    const getTimeSlotsByDay = () => {
        if (!doctor?.time_slots) return {};

        const groupedSlots = {};
        const usedDates = new Set(); // Track used dates to prevent duplicates
        
        console.log('=== Generating time slots by day ===');
        console.log('Available time slots:', doctor.time_slots);
        
        doctor.time_slots.forEach(slot => {
            if (!groupedSlots[slot.day_of_week]) {
                let nextDate = getNextDayOccurrence(slot.day_of_week);
                
                console.log(`Day ${slot.day_of_week} (${slot.day_name}): Initial date = ${nextDate.toDateString()}`);
                
                // Ensure unique dates - if date already used, get next week
                let attempts = 0;
                while (usedDates.has(nextDate.toDateString()) && attempts < 8) {
                    nextDate = new Date(nextDate);
                    nextDate.setDate(nextDate.getDate() + 7);
                    attempts++;
                    console.log(`Day ${slot.day_of_week}: Date conflict, trying next week: ${nextDate.toDateString()}`);
                }
                
                usedDates.add(nextDate.toDateString());
                const isBlocked = isDateInBreaks(nextDate);

                console.log(`Final date for ${slot.day_name}: ${nextDate.toDateString()} (${formatDate(nextDate)})`);

                groupedSlots[slot.day_of_week] = {
                    slots: [],
                    date: nextDate,
                    day_name: slot.day_name,
                    isBlocked: isBlocked
                };
            }
            groupedSlots[slot.day_of_week].slots.push(slot);
        });

        // Sort slots within each day by start time
        Object.values(groupedSlots).forEach(dayGroup => {
            dayGroup.slots.sort((a, b) => a.start_time.localeCompare(b.start_time));
        });

        console.log('Final grouped slots:', groupedSlots);
        console.log('===================================');

        return groupedSlots;
    };

    // Helper function to get doctor rating (placeholder)
    const getDoctorRating = () => {
        return 4.5; // Default rating
    };

    // Helper function to get review count (placeholder)
    const getReviewCount = () => {
        return Math.floor(Math.random() * 100 + 20);
    };


    if (loading) {
        return (
            <Box sx={{ bgcolor: isMobile ? '#f5f5f5' : 'inherit', minHeight: '100vh' }}>
                <Container>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress size={50} />
                    </Box>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ bgcolor: isMobile ? '#f5f5f5' : 'inherit', minHeight: '100vh' }}>
                <Container>
                    <Box sx={{ mt: 3 }}>
                        <IconButton onClick={handleBack} sx={{ mb: 2 }}>
                            <ArrowBack />
                        </IconButton>
                        <Alert severity="error" sx={{ mt: 2 }}>
                            {error}
                            <Button color="inherit" size="small" onClick={handleBack} sx={{ ml: 2 }}>
                                Return to Doctors List
                            </Button>
                        </Alert>
                    </Box>
                </Container>
            </Box>
        );
    }

    if (!doctor) {
        return (
            <Box sx={{ bgcolor: isMobile ? '#f5f5f5' : 'inherit', minHeight: '100vh' }}>
                <Container>
                    <Box sx={{ mt: 3 }}>
                        <IconButton onClick={handleBack} sx={{ mb: 2 }}>
                            <ArrowBack />
                        </IconButton>
                        <Alert severity="info" sx={{ mt: 2 }}>
                            Doctor not found
                            <Button color="inherit" size="small" onClick={handleBack} sx={{ ml: 2 }}>
                                Return to Doctors List
                            </Button>
                        </Alert>
                    </Box>
                </Container>
            </Box>
        );
    }

    // Group time slots by day
    const timeSlotsByDay = doctor?.time_slots?.reduce((acc, slot) => {
        if (!acc[slot.day_of_week]) {
            acc[slot.day_of_week] = [];
        }
        acc[slot.day_of_week].push(slot);
        return acc;
    }, {}) || {};

    // Check if there are any available slots
    const hasAvailableSlots = Object.keys(timeSlotsByDay).length > 0;
    const rating = getDoctorRating();
    const reviewCount = getReviewCount();

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Box sx={{ bgcolor: isMobile ? '#f5f5f5' : 'inherit', minHeight: '100vh', mb: 10 }}>
            <Container maxWidth="lg" sx={{ py: { xs: 2, md: 4 } }}>
                {/* Header with Back Button */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, md: 3 } }}>
                    <IconButton onClick={handleBack} sx={{ mr: 2 }}>
                        <ArrowBack />
                    </IconButton>
                    <Typography variant={isMobile ? "h5" : "h4"} component="h1">
                        Doctor Details
                    </Typography>
                </Box>

                {/* Mobile Layout */}
                {isMobile ? (
                    <Stack spacing={2}>
                        {/* Doctor Header Card */}
                        <Card sx={{ borderRadius: 3, overflow: 'hidden' }}>
                            <Box sx={{
                                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                    color: 'white',
                                    p: 2.5,
                                    position: 'relative'
                            }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5 }}>
                                    <Avatar
                                        src={doctor.profile_photo}
                                        alt={doctor.name}
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            border: '2px solid rgba(255,255,255,0.3)',
                                            boxShadow: '0 2px 12px rgba(0,0,0,0.2)'
                                        }}
                                    />
                                    <Box sx={{ flex: 1 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant="h6" sx={{ fontWeight: 700, color: 'white', fontSize: '1.1rem' }}>
                                                Dr. {doctor.name}
                                            </Typography>
                                            <Chip
                                                icon={<Verified sx={{ fontSize: 12 }} />}
                                                label="Verified"
                                                size="small"
                                                sx={{
                                                    bgcolor: 'rgba(255,255,255,0.2)',
                                                    color: 'white',
                                                    fontSize: '0.7rem',
                                                    height: 24,
                                                    '& .MuiChip-icon': { color: 'white' }
                                                }}
                                            />
                                        </Box>
                                        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.9)', mb: 0.5 }}>
                                            {doctor.specialty.name}
                                        </Typography>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Star sx={{ color: '#ffd700', fontSize: 16 }} />
                                            <Typography variant="body2" sx={{ color: 'white', fontWeight: 600, fontSize: '0.8rem' }}>
                                                {rating}
                                            </Typography>
                                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.8rem' }}>
                                                ({reviewCount} reviews)
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Card>

                        {/* Tabs */}
                        <Card sx={{ borderRadius: 2 }}>
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                variant="fullWidth"
                                sx={{
                                    bgcolor: 'background.paper',
                                    '& .MuiTab-root': {
                                        minHeight: 60,
                                        fontSize: '0.9rem',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        '&.Mui-selected': {
                                            color: 'primary.main',
                                        }
                                    },
                                    '& .MuiTabs-indicator': {
                                        backgroundColor: 'primary.main',
                                    }
                                }}
                            >
                                <Tab
                                    icon={<Schedule />}
                                    label="Schedule"
                                    iconPosition="start"
                                />
                                <Tab
                                    icon={<Person />}
                                    label="Profile"
                                    iconPosition="start"
                                />
                            </Tabs>
                        </Card>

                        {/* Tab Content */}
                        <TabPanel value={tabValue} index={0}>
                            {/* Schedule Tab */}
                            <Card sx={{ borderRadius: 2 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                                        <Schedule sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                        Available Time Slots
                                    </Typography>

                                    {!hasAvailableSlots ? (
                                        <Alert severity="info" sx={{ borderRadius: 2 }}>
                                            No BookMyOP slots available for this doctor.
                                        </Alert>
                                    ) : (
                                        <Stack spacing={3}>
                                            {/* Slot Duration Info */}
                                            {doctor.slot_duration_minutes && (
                                                <Box sx={{ p: 2, bgcolor: '#f8f9ff', borderRadius: 2 }}>
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600 }}>
                                                        <AccessTime sx={{ mr: 1, verticalAlign: 'middle', fontSize: 18 }} />
                                                    You can book appointments up to 3 months in advance.
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Date Selection */}
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                                                    Select Date (Available up to 3 months)
                                                </Typography>
                                                <Box sx={{ position: 'relative' }}>
                                                <DatePicker
                                                        value={selectedDate}
                                                        onChange={handleDateChange}
                                                        minDate={getDateRange().minDate}
                                                        maxDate={getDateRange().maxDate}
                                                        shouldDisableDate={(date) => {
                                                            return !isDateValidForDoctor(date) || isDateInBreaks(date);
                                                        }}
                                                        slotProps={{
                                                                textField: {
                                                                    fullWidth: true,
                                                                    size: 'small',
                                                                    label: 'Choose appointment date',
                                                                    sx: {
                                                                        '& .MuiOutlinedInput-root': {
                                                                            borderRadius: 2,
                                                                        }
                                                                    }
                                                            },
                                                            paper: {
                                                                    sx: {
                                                                        bgcolor: 'white',
                                                                        position: 'relative',
                                                                        pb: 5,
                                                                        '& .MuiPickersCalendarHeader-root': {
                                                                            bgcolor: 'white',
                                                                        },
                                                                        '& .MuiDayCalendar-weekContainer': {
                                                                            bgcolor: 'white',
                                                                        },
                                                                        '& .MuiDayCalendar-root': {
                                                                            position: 'relative',
                                                                            '&::after': {
                                                                                content: '"○ Available  ● Selected  ○ Blocked"',
                                                                                position: 'absolute',
                                                                                bottom: -38,
                                                                                left: 0,
                                                                                right: 0,
                                                                                textAlign: 'center',
                                                                                fontSize: '0.55rem',
                                                                                color: 'text.secondary',
                                                                                py: 0.75,
                                                                                px: 1,
                                                                                bgcolor: '#f8f9ff',
                                                                                borderTop: '1px solid',
                                                                                borderColor: 'primary.light',
                                                                                borderRadius: '0 0 4px 4px',
                                                                                fontWeight: 500,
                                                                                letterSpacing: '0.3px',
                                                                            }
                                                                        }
                                                                    }
                                                                },
                                                            day: (ownerState) => {
                                                                const date = ownerState.day;
                                                                const isSelected = ownerState.selected;
                                                                const isDisabled = ownerState.disabled;
                                                                const isAvailable = date && !isSelected && !isDisabled && isDateNotPast(date) && isDateValidForDoctor(date) && !isDateInBreaks(date);
                                                                
                                                                return {
                                                                    sx: {
                                                                        ...(isAvailable && {
                                                                            bgcolor: '#e3f2fd',
                                                                            borderRadius: '50%',
                                                                            border: '1px solid #1976d2',
                                                                            '&:hover': {
                                                                                bgcolor: '#bbdefb',
                                                                            }
                                                                        }),
                                                                        // Selected and disabled dates keep default styling
                                                                    }
                                                                };
                                                            }
                                                            }}
                                                            format="MMM dd, yyyy"
                                                        />
                                                    </Box>
                                                
                                                {/* Show available days hint */}
                                                <Box sx={{ mt: 1, p: 2, bgcolor: '#f8f9ff', borderRadius: 2 }}>
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                                                        Available Days:
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor?.time_slots?.map(slot => slot.day_name).join(', ') || 'No days available'}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Available Appointment Slots as Chips */}
                                            {selectedDate && (
                                                <Box>
                                                    {isDateInBreaks(selectedDate) ? (
                                                        <Alert severity="warning" sx={{ borderRadius: 2, mb: 2 }}>
                                                            <Typography variant="body2">
                                                                This date is unavailable due to doctor's break. Please select another date.
                                                            </Typography>
                                                        </Alert>
                                                    ) : (
                                                        <>
                                                            <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                                                                Available Times for {formatDate(selectedDate)}
                                                            </Typography>
                                                            {loadingAllSlots ? (
                                                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                                                    <CircularProgress size={30} />
                                                                </Box>
                                                            ) : slotsError ? (
                                                                <Alert severity="error" sx={{ borderRadius: 2 }}>
                                                                    {slotsError}
                                                                </Alert>
                                                            ) : availableSlots.length > 0 ? (
                                                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                                    {availableSlots.map((slot, index) => (
                                                                        <Chip
                                                                            key={index}
                                                                            label={`${new Date(`2000-01-01T${slot.start}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(`2000-01-01T${slot.end}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                                                            onClick={() => handleChipClick(slot)}
                                                                            disabled={slot.status === 'booked' || creatingAppointment}
                                                                            color={slot.status === 'available' ? 'primary' : 'default'}
                                                                            variant={slot.status === 'available' ? 'filled' : 'outlined'}
                                                                                sx={{
                                                                                fontSize: '0.9rem',
                                                                                fontWeight: 600,
                                                                                height: 40,
                                                                                cursor: slot.status === 'available' ? 'pointer' : 'default',
                                                                                opacity: slot.status === 'booked' ? 0.6 : 1,
                                                                                    '&:hover': {
                                                                                    opacity: slot.status === 'available' ? 0.8 : 0.6
                                                                                }
                                                                            }}
                                                                        />
                                                                    ))}
                                                                </Box>
                                                            ) : (
                                                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                                                    <Typography variant="body2">
                                                                        No appointment slots available for this date. Please select a different date.
                                                                    </Typography>
                                                                </Alert>
                                                            )}
                                                        </>
                                                    )}
                                                </Box>
                                            )}
                                        </Stack>
                                    )}
                                </CardContent>
                            </Card>
                        </TabPanel>

                        <TabPanel value={tabValue} index={1}>
                            {/* Doctor Profile Tab */}
                            <Stack spacing={2}>
                                {/* Quick Stats */}
                                <Card sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            Quick Info
                                        </Typography>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9ff', borderRadius: 2 }}>
                                                    <Typography variant="h6" color="primary" sx={{ fontWeight: 700 }}>
                                                        {doctor.experience_years}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        Years Experience
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f0fff4', borderRadius: 2 }}>
                                                    <Typography variant="h6" color="success.main" sx={{ fontWeight: 700 }}>
                                                        ₹{parseFloat(doctor.consultation_fee).toFixed(0)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary">
                                                        Consultation Fee
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>

                                {/* Doctor Details */}
                                <Card sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            <Person sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                            Doctor Details
                                        </Typography>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    Qualifications
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {doctor.qualifications?.name || doctor.qualifications?.abbreviation || 'N/A'}
                                                </Typography>
                                            </Box>
                                            {doctor.registration_number && (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        Registration Number
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor.registration_number}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    Experience
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {doctor.experience_years} years in {doctor.specialty.name}
                                                </Typography>
                                            </Box>
                                        </Stack>
                                    </CardContent>
                                </Card>

                                {/* Hospital Details */}
                                <Card sx={{ borderRadius: 2 }}>
                                    <CardContent sx={{ p: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                                            <LocalHospital sx={{ mr: 1, verticalAlign: 'middle', color: 'primary.main' }} />
                                            Hospital Details
                                        </Typography>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    {doctor.service_provider.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {doctor.service_provider.provider_type_name}
                                                </Typography>
                                            </Box>
                                            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                                                <LocationOn sx={{ color: 'text.primary', mt: 0.5, fontSize: 20 }} />
                                                <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                                                    {doctor.service_provider.address}
                                                </Typography>
                                            </Box>
                                            {doctor.service_provider.coordinator_contact && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Phone sx={{ color: 'text.primary', fontSize: 20 }} />
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor.service_provider.coordinator_contact}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {doctor.service_provider.bed_count && (
                                                <Box>
                                                    <Typography variant="body2" color="text.primary">
                                                        <strong>Bed Capacity:</strong> {doctor.service_provider.bed_count} beds
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </CardContent>
                                </Card>

                                {/* Discount Info */}
                                {doctor.discount_percentage > 0 && (
                                    <Card sx={{ borderRadius: 2, bgcolor: '#fff3e0' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Discount sx={{ color: 'orange', fontSize: 30 }} />
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    {doctor.discount_percentage}% 
                                                    </Typography>
                                                    <Typography variant="body1" color="text.primary">
                                                       Discount Available
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}

                                {/* Upcoming Breaks */}
                                {doctor.upcoming_breaks && doctor.upcoming_breaks.length > 0 && (
                                    <Card sx={{ borderRadius: 2, bgcolor: '#fff5f5' }}>
                                        <CardContent sx={{ p: 3 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <AccessTime sx={{ color: 'error.main', fontSize: 30 }} />
                                                <Box>
                                                    <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                        Upcoming Breaks
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary" sx={{ mt: 1 }}>
                                                        Doctor will be unavailable on:
                                                    </Typography>
                                                    <Box sx={{ mt: 1 }}>
                                                        {doctor.upcoming_breaks
                                                            .filter(breakItem => breakItem.is_active)
                                                            .map((breakItem, index) => (
                                                                <Chip
                                                                    key={breakItem.id}
                                                                    label={new Date(breakItem.break_date).toLocaleDateString('en-US', {
                                                                        weekday: 'long',
                                                                        year: 'numeric',
                                                                        month: 'long',
                                                                        day: 'numeric'
                                                                    })}
                                                                    color="error"
                                                                    variant="outlined"
                                                                    size="small"
                                                                    sx={{ mr: 1, mb: 1 }}
                                                                />
                                                            ))}
                                                    </Box>
                                                </Box>
                                            </Box>
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                        </TabPanel>
                    </Stack>
                ) : (
                    /* Desktop Layout - Enhanced with all missing information */
                    <Grid container spacing={4}>
                        {/* Doctor Profile Section */}
                        <Grid item xs={12} md={4}>
                            <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Box display="flex" flexDirection="column" alignItems="center" mb={3}>
                                        <Avatar
                                            src={doctor.profile_photo ? `${config.backend.host}${doctor.profile_photo}` : null}
                                            alt={doctor.name}
                                            sx={{ 
                                                width: 150, 
                                                height: 150, 
                                                mb: 2,
                                                border: '3px solid',
                                                borderColor: 'primary.main',
                                                boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                        <Typography variant="h4" gutterBottom sx={{ fontWeight: 700, textAlign: 'center' }}>
                                            Dr. {doctor.name}
                                        </Typography>
                                        <Typography variant="h6" color="primary" gutterBottom sx={{ fontWeight: 600 }}>
                                            {doctor.specialty.name}
                                        </Typography>
                                        
                                        {/* Star Rating */}
                                        {doctor.star_rating && (
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                                <Star sx={{ color: '#ffd700', fontSize: 20 }} />
                                                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                                    {doctor.star_rating}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    (Verified)
                                                </Typography>
                                            </Box>
                                        )}
                                    </Box>

                                    <Divider sx={{ my: 3 }} />

                                    {/* Quick Stats */}
                                    <Box sx={{ mb: 3 }}>
                                        <Grid container spacing={2}>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9ff', borderRadius: 2, border: '1px solid', borderColor: 'primary.light' }}>
                                                    <Typography variant="h5" color="primary" sx={{ fontWeight: 700 }}>
                                                        {doctor.experience_years}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                                                        Years Experience
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                            <Grid item xs={6}>
                                                <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f0fff4', borderRadius: 2, border: '1px solid', borderColor: 'success.light' }}>
                                                    <Typography variant="h5" color="success.main" sx={{ fontWeight: 700 }}>
                                                        ₹{parseFloat(doctor.consultation_fee).toFixed(0)}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.primary" sx={{ fontWeight: 600 }}>
                                                        Consultation Fee
                                                    </Typography>
                                                </Box>
                                            </Grid>
                                        </Grid>
                                    </Box>

                                    {/* Doctor Details */}
                                    <Box sx={{ mb: 3 }}>
                                        <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <Person sx={{ color: 'primary.main' }} />
                                            Doctor Details
                                        </Typography>
                                        <Stack spacing={2}>
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                                    Qualifications
                                                </Typography>
                                                <Typography variant="body2" color="text.primary">
                                                    {doctor.qualifications?.name || doctor.qualifications?.abbreviation || 'N/A'}
                                                </Typography>
                                            </Box>
                                            
                                            {doctor.registration_number && (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                                        Registration Number
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor.registration_number}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {doctor.mobile_number && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Phone sx={{ color: 'text.primary', fontSize: 18 }} />
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor.mobile_number}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {doctor.email && (
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <Email sx={{ color: 'text.primary', fontSize: 18 }} />
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor.email}
                                                    </Typography>
                                                </Box>
                                            )}

                                            {doctor.bio && (
                                                <Box>
                                                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                                                        Bio
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor.bio}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Stack>
                                    </Box>

                                    {/* Discount Info */}
                                    {doctor.discount_percentage > 0 && (
                                        <Box sx={{ p: 2, bgcolor: '#fff3e0', borderRadius: 2, border: '1px solid', borderColor: 'warning.light' }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Discount sx={{ color: 'warning.main', fontSize: 24 }} />
                                                <Box>
                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                                    {doctor.discount_percentage}% 
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                       Discount Available
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Hospital Details and Time Slots Section */}
                        <Grid item xs={12} md={8}>
                            {/* Hospital Details */}
                            <Card sx={{ mb: 4, borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                        <LocalHospital sx={{ color: 'primary.main' }} />
                                        Hospital Details
                                    </Typography>
                                    
                                    {/* Hospital Building Image */}
                                    {doctor.service_provider.building_image && (
                                        <Box sx={{ mb: 3 }}>
                                            <Box
                                                component="img"
                                                src={`${config.backend.host}${doctor.service_provider.building_image}`}
                                                alt={`${doctor.service_provider.name} building`}
                                                sx={{
                                                    width: '100%',
                                                    height: 200,
                                                    objectFit: 'cover',
                                                    borderRadius: 2,
                                                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                                                }}
                                            />
                                        </Box>
                                    )}

                                    <Grid container spacing={3}>
                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                                    {doctor.service_provider.name}
                                                </Typography>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    {doctor.service_provider.provider_type_name}
                                                </Typography>

                                                {/* Hospital Rating */}
                                                {doctor.service_provider.star_rating && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <Star sx={{ color: '#ffd700', fontSize: 18 }} />
                                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                            {doctor.service_provider.star_rating}
                                                        </Typography>
                                                        <Typography variant="body2" color="text.secondary">
                                                            Hospital Rating
                                                        </Typography>
                                                    </Box>
                                                )}

                                                {/* Bed Count */}
                                                {doctor.service_provider.bed_count && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                                        <LocalHospital sx={{ color: 'primary.main', fontSize: 18 }} />
                                                        <Typography variant="body2" color="text.primary">
                                                            <strong>{doctor.service_provider.bed_count}</strong> beds capacity
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </Box>
                                        </Grid>

                                        <Grid item xs={12} md={6}>
                                            <Box>
                                                {/* Address */}
                                                <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 2 }}>
                                                    <LocationOn sx={{ color: 'primary.main', mt: 0.5, fontSize: 20 }} />
                                                    <Box>
                                                        <Typography variant="body2" color="text.primary" sx={{ lineHeight: 1.5 }}>
                                                            {doctor.service_provider.address_line1 && `${doctor.service_provider.address_line1}, `}
                                                            {doctor.service_provider.address_line2 && `${doctor.service_provider.address_line2}, `}
                                                            {doctor.service_provider.locality && `${doctor.service_provider.locality}, `}
                                                            {doctor.service_provider.city && `${doctor.service_provider.city}, `}
                                                            {doctor.service_provider.state && `${doctor.service_provider.state} `}
                                                            {doctor.service_provider.pincode && `- ${doctor.service_provider.pincode}`}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                {/* Contact Information */}
                                                {doctor.service_provider.coordinator_1_number && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Phone sx={{ color: 'primary.main', fontSize: 18 }} />
                                                        <Box>
                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                                                                {doctor.service_provider.coordinator_1_name || 'Coordinator'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.primary">
                                                                {doctor.service_provider.coordinator_1_number}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}

                                                {doctor.service_provider.coordinator_2_number && (
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                        <Phone sx={{ color: 'primary.main', fontSize: 18 }} />
                                                        <Box>
                                                            <Typography variant="body2" color="text.primary" sx={{ fontWeight: 600 }}>
                                                                {doctor.service_provider.coordinator_2_name || 'Coordinator 2'}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.primary">
                                                                {doctor.service_provider.coordinator_2_number}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}

                                                {/* License Info */}
                                                {/* {doctor.service_provider.license_number && (
                                                    <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9ff', borderRadius: 2 }}>
                                                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', mb: 1 }}>
                                                            License Information
                                                        </Typography>
                                                        <Typography variant="body2" color="text.primary">
                                                            License: {doctor.service_provider.license_number}
                                                        </Typography>
                                                        {/* {doctor.service_provider.license_expiry && (
                                                            <Typography variant="body2" color="text.primary">
                                                                Expires: {new Date(doctor.service_provider.license_expiry).toLocaleDateString()}
                                                            </Typography>
                                                        )} }
                                                    </Box>
                                                )} */}
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>

                            {/* Time Slots Section */}
                            <Card sx={{ borderRadius: 3, overflow: 'hidden', boxShadow: 3 }}>
                                <CardContent sx={{ p: 3 }}>
                                    <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                                        <Schedule sx={{ color: 'primary.main' }} />
                                        Available Time Slots
                                    </Typography>

                                    {!hasAvailableSlots ? (
                                        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                            No BookMyOP slots available for this doctor.
                                        </Alert>
                                    ) : (
                                        <>
                                            {/* Slot Duration Info */}
                                            {doctor.slot_duration_minutes && (
                                                <Box sx={{ p: 2, bgcolor: '#f8f9ff', borderRadius: 2, mb: 3, border: '1px solid', borderColor: 'primary.light' }}>
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccessTime sx={{ fontSize: 18 }} />
                                                        You can book appointments up to 3 months in advance.
                                                    </Typography>
                                                </Box>
                                            )}

                                            {/* Date Selection */}
                                            <Box sx={{ mb: 3 }}>
                                                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                                                    Select Date (Available up to 3 months)
                                                </Typography>
                                                <DatePicker
                                                        value={selectedDate}
                                                        onChange={handleDateChange}
                                                        minDate={getDateRange().minDate}
                                                        maxDate={getDateRange().maxDate}
                                                        shouldDisableDate={(date) => {
                                                            return !isDateValidForDoctor(date) || isDateInBreaks(date);
                                                        }}
                                                        slotProps={{
                                                            textField: {
                                                                fullWidth: true,
                                                                size: 'medium',
                                                                label: 'Choose appointment date',
                                                                sx: {
                                                                    '& .MuiOutlinedInput-root': {
                                                                        borderRadius: 2,
                                                                    }
                                                                }
                                                            },
                                                            paper: {
                                                                sx: {
                                                                    bgcolor: 'white',
                                                                    position: 'relative',
                                                                    pb: 5,
                                                                    '& .MuiPickersCalendarHeader-root': {
                                                                        bgcolor: 'white',
                                                                    },
                                                                    '& .MuiDayCalendar-weekContainer': {
                                                                        bgcolor: 'white',
                                                                    },
                                                                    '& .MuiDayCalendar-root': {
                                                                        position: 'relative',
                                                                        '&::after': {
                                                                            content: '"Available • Selected • Blocked"',
                                                                            position: 'absolute',
                                                                            bottom: -40,
                                                                            left: 0,
                                                                            right: 0,
                                                                            textAlign: 'center',
                                                                            fontSize: '0.65rem',
                                                                            color: 'text.secondary',
                                                                            py: 1,
                                                                            px: 1,
                                                                            bgcolor: '#f8f9ff',
                                                                            borderTop: '1px solid',
                                                                            borderColor: 'primary.light',
                                                                            borderRadius: '0 0 4px 4px',
                                                                        }
                                                                    }
                                                                }
                                                            },
                                                            day: (ownerState) => {
                                                                const date = ownerState.day;
                                                                const isSelected = ownerState.selected;
                                                                const isDisabled = ownerState.disabled;
                                                                const isAvailable = date && !isSelected && !isDisabled && isDateNotPast(date) && isDateValidForDoctor(date) && !isDateInBreaks(date);
                                                                
                                                                return {
                                                                    sx: {
                                                                        ...(isAvailable && {
                                                                            bgcolor: '#e3f2fd',
                                                                            borderRadius: '50%',
                                                                            border: '1px solid #1976d2',
                                                                            '&:hover': {
                                                                                bgcolor: '#bbdefb',
                                                                            }
                                                                        }),
                                                                        // Selected and disabled dates keep default styling
                                                                    }
                                                                };
                                                            }
                                                        }}
                                                        format="MMM dd, yyyy"
                                                    />
                                                
                                                {/* Show available days hint */}
                                                <Box sx={{ mt: 2, p: 2, bgcolor: '#f8f9ff', borderRadius: 2, border: '1px solid', borderColor: 'primary.light' }}>
                                                    <Typography variant="body2" color="primary" sx={{ fontWeight: 600, mb: 1 }}>
                                                        Available Days:
                                                    </Typography>
                                                    <Typography variant="body2" color="text.primary">
                                                        {doctor?.time_slots?.map(slot => slot.day_name).join(', ') || 'No days available'}
                                                    </Typography>
                                                </Box>
                                            </Box>

                                            {/* Available Appointment Slots as Chips */}
                                            {selectedDate && (
                                                <>
                                                    {isDateInBreaks(selectedDate) ? (
                                                        <Alert severity="warning" sx={{ mt: 2, borderRadius: 2 }}>
                                                            <Typography variant="body2">
                                                                This date is unavailable due to doctor's break. Please select another date.
                                                            </Typography>
                                                        </Alert>
                                                    ) : loadingAllSlots ? (
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                                            <CircularProgress size={40} />
                                                        </Box>
                                                    ) : slotsError ? (
                                                        <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                                                            {slotsError}
                                                        </Alert>
                                                    ) : availableSlots.length > 0 ? (
                                                        <Box sx={{ mt: 2 }}>
                                                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'text.primary' }}>
                                                                Available Appointment Times
                                                            </Typography>
                                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
                                                                {availableSlots.map((slot, index) => (
                                                                    <Chip
                                                                        key={index}
                                                                        label={`${new Date(`2000-01-01T${slot.start}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(`2000-01-01T${slot.end}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`}
                                                                        onClick={() => handleChipClick(slot)}
                                                                        disabled={slot.status === 'booked' || creatingAppointment}
                                                                        color={slot.status === 'available' ? 'primary' : 'default'}
                                                                        variant={slot.status === 'available' ? 'filled' : 'outlined'}
                                                                                    sx={{ 
                                                                            fontSize: '0.95rem',
                                                                                        fontWeight: 600,
                                                                            height: 42,
                                                                            px: 2,
                                                                            cursor: slot.status === 'available' ? 'pointer' : 'default',
                                                                            opacity: slot.status === 'booked' ? 0.6 : 1,
                                                                            '&:hover': {
                                                                                opacity: slot.status === 'available' ? 0.8 : 0.6,
                                                                                transform: slot.status === 'available' ? 'scale(1.05)' : 'none',
                                                                                transition: 'all 0.2s ease'
                                                                            }
                                                                        }}
                                                                    />
                                                                ))}
                                                            </Box>
                                                        </Box>
                                                    ) : (
                                                        <Alert severity="info" sx={{ mt: 2, borderRadius: 2 }}>
                                                            <Typography variant="body2">
                                                                No appointment slots available for {formatDate(selectedDate)}. Please select a different date.
                                                            </Typography>
                                                        </Alert>
                                                    )}
                                                </>
                                            )}
                                        </>
                                    )}
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                )}
            </Container>

            {/* Confirmation Dialog */}
            <Dialog
                open={confirmationDialog}
                onClose={handleCancelBooking}
                maxWidth="xs"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: isMobile ? 3 : 2 }
                }}
            >
                <DialogTitle sx={{ fontWeight: 600 }}>Proceed to  Request</DialogTitle>
                <DialogContent>
                    <DialogContentText sx={{ mb: 2 }}>
                        Please confirm your booking details:
                    </DialogContentText>
                    <Box sx={{ mt: 2 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                            Doctor: Dr. {doctor?.name}
                            </Typography>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                            Specialty: {doctor?.specialty?.name}
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                            Date & Time: {selectedSlotForBooking && selectedDate
                                ? `${formatDate(selectedDate)}, ${new Date(`2000-01-01T${selectedSlotForBooking.start}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} - ${new Date(`2000-01-01T${selectedSlotForBooking.end}`).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`
                                : ''}
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 3, pt: 2 }}>
                    <Button
                        onClick={handleCancelBooking} 
                        color="primary"
                        disabled={creatingAppointment}
                        sx={{ borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmBooking} 
                        variant="contained"
                        color="primary"
                        disabled={creatingAppointment}
                        sx={{ borderRadius: 2 }}
                    >
                        {creatingAppointment ? (
                            <CircularProgress size={20} color="inherit" />
                        ) : (
                            'Request Appointment'
                        )}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add Snackbar for notifications */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleCloseSnackbar} severity={snackbar.severity} sx={{ width: '100%', borderRadius: 2 }}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
        </LocalizationProvider>
    );
};

export default DoctorDetail;
